import React, { useState } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, CircularProgress, Alert } from "@mui/material";
import api from "../../api";

/**
 * StatsControls
 * - Trois boutons : reset all / reset matches / reset arbitrages
 * - Bouton supplémentaire : reconstruit les stats depuis la collection match (recompute)
 * - Confirme explicitement l'action (dialogue) : l'utilisateur doit cliquer "Confirmer" puis "Fermer"
 * - Appelle les endpoints POST:
 *     /api/stats/resetAll
 *     /api/stats/resetMatches
 *     /api/stats/resetArbitrages
 *     /api/stats/recompute
 *
 * Props:
 * - onDone: optional callback invoked after a successful operation so the parent can refresh data
 *
 * Notes:
 * - If a sessionStorage.profAuthToken exists (set by SettingsAuthModal), this token will be used for the requests.
 */

export default function StatsControls({ onDone } = {}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState(null); // 'all' | 'matches' | 'arbitrages' | 'recompute'
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [completed, setCompleted] = useState(false);

  function openConfirm(action) {
    setCurrentAction(action);
    setMsg(null);
    setCompleted(false);
    setDialogOpen(true);
  }

  function closeConfirm() {
    if (loading) return;
    setDialogOpen(false);
    setCurrentAction(null);
    setMsg(null);
    setCompleted(false);
  }

  async function doAction() {
    if (!currentAction) return;
    setLoading(true);
    setMsg(null);
    try {
      let url = "/api/stats/resetAll";
      if (currentAction === "matches") url = "/api/stats/resetMatches";
      else if (currentAction === "arbitrages") url = "/api/stats/resetArbitrages";
      else if (currentAction === "recompute") url = "/api/stats/recompute";

      // If a profAuthToken is present in sessionStorage, use it for this request (temporary elevated access)
      const profToken = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("profAuthToken") : null;
      const config = {};
      if (profToken) {
        config.headers = { Authorization: `Bearer ${profToken}` };
      }

      const res = await api.post(url, {}, config);
      const data = res?.data || {};
      setMsg({ type: "success", text: data?.message || "Opération réussie" });
      setCompleted(true);
      // notify parent to refresh data
      if (typeof onDone === "function") {
        try { onDone(currentAction); } catch (e) { /* ignore */ }
      }
    } catch (err) {
      console.error("Stats action error:", err);
      const text = err?.response?.data?.error || err?.message || "Erreur lors de l'opération";
      setMsg({ type: "error", text });
      setCompleted(false);
    } finally {
      setLoading(false);
    }
  }

  const titleMap = {
    all: "Réinitialiser toutes les statistiques",
    matches: "Réinitialiser les résultats des matchs",
    arbitrages: "Réinitialiser les arbitrages",
    recompute: "Reconstruire les statistiques depuis l'historique"
  };
  const helperMap = {
    all: "Remettra à zéro tous les compteurs (victoires/défaites/égalité/arbitrages). Action irréversible.",
    matches: "Remettra à zéro uniquement les compteurs de victoires/défaites/égalité (les autres compteurs sont conservés).",
    arbitrages: "Remettra à zéro uniquement le compteur d'arbitrages.",
    recompute: "Relira tous les matchs enregistrés et recalculera les statistiques (recommandé en cas d'incohérence)."
  };

  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mt: 2 }}>
      <Button variant="outlined" color="error" onClick={() => openConfirm("all")}>Réinitialiser toutes les stats</Button>
      <Button variant="outlined" color="warning" onClick={() => openConfirm("matches")}>Réinitialiser résultats des matchs</Button>
      <Button variant="outlined" color="primary" onClick={() => openConfirm("arbitrages")}>Réinitialiser les arbitrages</Button>
      <Button variant="contained" color="secondary" onClick={() => openConfirm("recompute")}>Reconstruire depuis l'historique</Button>

      <Dialog open={dialogOpen} onClose={() => closeConfirm()}>
        <DialogTitle>{titleMap[currentAction]}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>{helperMap[currentAction]}</Typography>

          {msg && <Alert severity={msg.type} sx={{ mb: 1 }}>{msg.text}</Alert>}

          {!completed && !loading && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Cliquez sur "Confirmer" pour lancer l'opération. Le dialogue restera ouvert jusqu'à ce que vous cliquiez sur "Fermer".
            </Typography>
          )}

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => closeConfirm()} disabled={loading}>Fermer</Button>
          <Button
            color={currentAction === "all" ? "error" : "primary"}
            onClick={() => doAction()}
            disabled={loading || completed}
            variant="contained"
          >
            {loading ? "Traitement..." : (completed ? "Terminé" : "Confirmer")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}